/**
 * Lincoln MoneyGuard 2025 Pricing Configuration
 *
 * Corrections for Lincoln MoneyGuard 2025's non-linear pricing. Higher premiums receive
 * proportionally better rates than simple linear scaling would suggest.
 * `getLincolnScalingFactor` (lincolnPricingUtils.ts) picks the correction by pay structure.
 *
 * - Single pay: premium band multipliers, anchored at 1.0 on the $100,000 the quotes are
 *   scraped at. Auto-generated from pricing model analysis on 2026-01-20.
 * - 10-Pay: a rule on the quote's own face amount, measured against the carrier on
 *   2026-10-06. See its own comment.
 */

/**
 * Lincoln issues MoneyGuard at a face amount of $50,000 to $500,000. A premium that would buy
 * more is refused outright by the illustration engine ("Allowable Range 50,000 - 500,000"),
 * for single pay and 10-Pay alike, so a re-priced quote must stop below it. Stored single pay
 * rows already carry an investmentMax inside this limit; 10-Pay rows carry a blanket $30,000
 * that the larger quotes cannot reach.
 */
export const LINCOLN_MONEYGUARD_2025_MAX_FACE_AMOUNT = 500000;

export const LINCOLN_MONEYGUARD_2025_BANDS = [
  { premium: 50000, multiplier: 0.9772 },
  { premium: 60000, multiplier: 0.9851 },
  { premium: 75000, multiplier: 0.9928 },
  { premium: 85000, multiplier: 0.9963 },
  { premium: 100000, multiplier: 1 },
  { premium: 115000, multiplier: 1.0027 },
  { premium: 125000, multiplier: 1.0041 },
  { premium: 150000, multiplier: 1.0068 },
  { premium: 175000, multiplier: 1.0087 },
  { premium: 200000, multiplier: 1.0102 },
  { premium: 225000, multiplier: 1.0113 },
  { premium: 250000, multiplier: 1.0122 },
  { premium: 300000, multiplier: 1.0136 },
  { premium: 350000, multiplier: 1.0146 },
  { premium: 400000, multiplier: 1.0153 },
  { premium: 500000, multiplier: 1.016 },
];

/**
 * 10-Pay is priced by a rule on the quote's own face amount rather than a table:
 *
 *   annual premium = annualCharge
 *                  + rate x face amount
 *                  + smallFaceSurchargeRate x max(smallFaceThreshold - face amount, 0)
 *
 * `rate` is the quote's own and is read off its stored premium and face amount. The other
 * three numbers are the same for every sex, class, age and plan. So $249 of each year's
 * premium buys no benefit, and a face amount under $100,000 costs a further $0.62 for every
 * $1,000 it falls short (which is why a premium-keyed table cannot hold this: where a quote
 * crosses $100,000 depends on the quote).
 *
 * Measured with 106 live WinFlex illustrations across both sexes, both classes, ages 40-70
 * and eleven of the twelve plan types (packages/scraping/tools/moneyguardPremiumLadder.js;
 * record in packages/scraping/HANDOFF.md section 15.8). The rule lands within $4 of the
 * carrier on all 90 points priced away from the stored premium. Monthly LTC, total LTC and
 * the residual death benefit all stay a fixed share of the face amount, so the one factor
 * covers every benefit.
 *
 * `maxPremium` is the highest annual premium the rule was measured at. Nothing above it is
 * priced, for the same reason nothing above a band table's last row is.
 */
export const LINCOLN_MONEYGUARD_2025_TEN_PAY_PRICING = {
  annualCharge: 249,
  smallFaceThreshold: 100000,
  smallFaceSurchargeRate: 0.00062,
  maxPremium: 30000,
} as const;
